import { FC } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { getTags } from '../api/tags';
import BackButton from '../components/ui/BackButton';
import Button from '../components/ui/Button';
import FileUpload from '../components/ui/FileUpload';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../components/ui/Form';
import Input from '../components/ui/Input';
import MultiSelector from '../components/ui/MultiSelector';
import { Option } from '../types';
import TextArea from './ui/TextArea';
import { useQuery } from '@tanstack/react-query';

type Props = {
  onSubmit: () => void;
  form: UseFormReturn<any>;
};

const AddNewAssetsForm: FC<Props> = ({ onSubmit, form }) => {
  const { data } = useQuery({
    queryKey: ['tags'],
    queryFn: () => getTags(),
    staleTime: 1000 * 30,
  });

  const tags = data?.results.results || [];

  return (
    <div className='mx-auto flex w-full max-w-2xl flex-1 flex-col gap-6 p-4'>
      <div className='flex flex-col gap-3'>
        <div className='flex flex-row gap-2'>
          <BackButton />

          <div className='text-sm font-semibold text-green-blue-0'>
            <h2 className='text-green-blue-0'>New Asset</h2>

            <div className='text-green-blue-2'>Asset input details</div>
          </div>
        </div>

        <Form {...form}>
          <form className='flex flex-1 flex-col gap-4 overflow-y-auto'>
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem className='flex flex-col gap-1'>
                  <FormControl>
                    <Input
                      {...field}
                      label='Asset Name'
                      placeholder='Pick a name'
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='metadata'
              render={({ field }) => (
                <FormItem className='flex flex-col gap-1'>
                  <FormControl>
                    <MultiSelector
                      value={field.value || []}
                      setValue={(val: Option[]) => field.onChange(val)}
                      label='Metadata (tags)'
                      placeholder='Metadata tags'
                      options={tags.map((tag) => ({
                        value: tag.uuid,
                        label: tag.name,
                      }))}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='thumbnail'
              render={({ field }) => (
                <FormItem className='flex flex-col gap-1'>
                  <FormLabel className='block text-sm font-semibold text-green-blue-0'>
                    Thumbnail
                  </FormLabel>

                  <FormControl>
                    <FileUpload
                      name='img-upload'
                      file={field.value || null}
                      onChange={(val) => field.onChange(val)}
                      label='Upload Cover'
                      type='img'
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='content'
              render={({ field }) => (
                <FormItem className='flex flex-col gap-1'>
                  <FormControl>
                    <Input {...field} label='Content' placeholder='Link' />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='description'
              render={({ field }) => (
                <FormItem className='flex flex-col gap-1'>
                  <FormControl>
                    <TextArea
                      {...field}
                      label='Description'
                      placeholder='Write a description'
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='document'
              render={({ field }) => {
                const isFile = field.value instanceof File;
                const isLink =
                  typeof field.value === 'string' && field.value.length > 0;

                return (
                  <FormItem className='flex flex-col gap-1'>
                    <FormLabel className='block text-sm font-semibold text-green-blue-0'>
                      DRM Policy source
                    </FormLabel>

                    <FormControl>
                      <div className='flex flex-col gap-2'>
                        {!isFile && (
                          <Input
                            {...field}
                            value={isLink ? field.value : ''}
                            placeholder='Link'
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                        )}

                        {!isLink && (
                          <FileUpload
                            name='document-upload'
                            label='Upload Policy source'
                            file={isFile ? field.value : null}
                            onChange={(file) => field.onChange(file)}
                            type='document'
                          />
                        )}
                      </div>
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </form>
        </Form>
      </div>

      <div className='mt-auto'>
        <Button
          disabled={!form.formState.isValid}
          type='submit'
          onClick={form.handleSubmit(onSubmit)}
          variant='solid'
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default AddNewAssetsForm;
